/**
 * Active Conversation Store (client-side, UI-only)
 *
 * Tracks the conversation the user is currently viewing so the shared-socket
 * chat listener (`useChatRealtime`) can suppress a transient toast for a chat
 * the user is already reading. Set by `ChatPage` (channel) and `SettingsPage`
 * (support ticket).
 *
 * This is the sole survivor of the former in-memory chat-notification store:
 * durable bell entries now arrive as server `notification.created` events
 * (type `chat_message`) via `useNotificationStore`. Only the toast-suppression
 * hint needs client state, so it lives here on its own.
 */

import { create } from "zustand";
import { devtools } from "zustand/middleware";

/**
 * Identifies the conversation the user is actively viewing. A channel chat sets
 * `channelId`; a support ticket sets `ticketId`.
 */
export interface ActiveConversation {
  channelId?: string;
  ticketId?: string;
}

interface ActiveConversationStore {
  activeConversation: ActiveConversation | null;
  setActiveConversation: (conversation: ActiveConversation | null) => void;
  clearActiveConversation: () => void;
}

export const useActiveConversationStore = create<ActiveConversationStore>()(
  devtools(
    (set) => ({
      activeConversation: null,

      setActiveConversation: (conversation) => {
        set(
          { activeConversation: conversation },
          false,
          "setActiveConversation",
        );
      },

      clearActiveConversation: () => {
        set({ activeConversation: null }, false, "clearActiveConversation");
      },
    }),
    {
      name: "active-conversation-store",
      enabled: import.meta.env.DEV,
    },
  ),
);
